// OIDC-Auth fuer die persoenliche /mein-Ansicht (Keycloak Realm glasbrenner).
// Nur fuer Mitarbeiter mit Keycloak-Account -- der Kiosk-Modus braucht das nicht.

import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

const KEYCLOAK_URL = 'https://auth.it-sce.de'
const REALM = 'glasbrenner'
const CLIENT_ID = 'stempel-pwa'

const base = window.location.origin

// offline_access (optionaler Client-Scope in Keycloak) liefert einen Offline-Refresh-Token
// (offlineIdle 30 Tage): Mitarbeiter melden sich einmal an und bleiben wochenlang angemeldet,
// unabhaengig von ssoSessionIdle/-Max. Lehnt der Server den Scope ab (error=invalid_scope),
// faellt login() dauerhaft auf den alten Scope zurueck (siehe markOfflineAccessAbgelehnt).
const SCOPE_BASIS = 'openid profile'
const SCOPE_MIT_OFFLINE = `${SCOPE_BASIS} offline_access`
const NO_OFFLINE_KEY = 'stempel-no-offline-access'

function offlineAccessAbgelehnt(): boolean {
  try {
    return window.localStorage.getItem(NO_OFFLINE_KEY) === '1'
  } catch {
    return false
  }
}

/** Merkt sich, dass Keycloak offline_access ablehnt; Aufrufer startet danach login() neu. */
export function markOfflineAccessAbgelehnt() {
  try {
    window.localStorage.setItem(NO_OFFLINE_KEY, '1')
  } catch {
    // ohne localStorage: Fallback gilt nur fuer diesen Aufruf
  }
}

// Token-Speicher: localStorage statt oidc-client-ts-Default (sessionStorage). Entscheidung
// (TQ #553, QM Review 569): Ziel ist "wochenlang angemeldet"; sessionStorage waere beim
// Schliessen der PWA weg und der 30-Tage-Offline-Token damit wertlos. Abwaegung: bei XSS ist
// der Refresh-Token (30 Tage) lesbar, nicht nur das 5-Minuten-Access-Token -- akzeptiert, weil
// die PWA keine fremden Inhalte rendert und nur auf Geraeten der Mitarbeiter laeuft.
// Gegenmassnahme bei geteilten/verlorenen Geraeten: revokeTokensOnSignout widerruft den
// refresh_token beim Logout (signoutRedirect ruft das vor dem Redirect auf).
// Ein frueher in sessionStorage gespeicherter User wird nicht migriert (einmaliger Re-Login).
function createUserStore(): WebStorageStateStore | undefined {
  try {
    const store = window.localStorage
    const probe = '__stempel_probe__'
    store.setItem(probe, '1')
    store.removeItem(probe)
    return new WebStorageStateStore({ store })
  } catch {
    // localStorage nicht nutzbar (z. B. blockiert): oidc-client-ts-Default (sessionStorage)
    return undefined
  }
}

export const userManager = new UserManager({
  authority: `${KEYCLOAK_URL}/realms/${REALM}`,
  client_id: CLIENT_ID,
  redirect_uri: `${base}/`,
  post_logout_redirect_uri: `${base}/`,
  scope: SCOPE_MIT_OFFLINE,
  response_type: 'code',
  userStore: createUserStore(),
  revokeTokensOnSignout: true,
  revokeTokenTypes: ['refresh_token'],
  // Aus: Die Erneuerung laeuft ausschliesslich ueber getFreshAccessToken (Single-Flight) und
  // den Token-Keeper. Zusaetzliche parallele signinSilent-Aufrufe der Bibliothek wuerden bei
  // spaeter eingeschalteter Refresh-Token-Rotation (heute: revokeRefreshToken=false) zu
  // invalid_grant fuehren. Rotation wird hier weiterhin NICHT vorausgesetzt.
  automaticSilentRenew: false,
  silent_redirect_uri: `${base}/silent`,
})

export async function login() {
  await userManager.signinRedirect({
    scope: offlineAccessAbgelehnt() ? SCOPE_BASIS : SCOPE_MIT_OFFLINE,
  })
}

export async function logout() {
  try {
    await userManager.signoutRedirect()
  } catch {
    // Widerruf/Abmelden am Server gescheitert (z. B. offline): lokal trotzdem abmelden, damit der
    // 30-Tage-Token nicht im localStorage bleibt. Der Widerruf am Server entfaellt dann.
    await userManager.removeUser()
    window.location.assign(`${base}/`)
  }
}

export async function getUser(): Promise<User | null> {
  return userManager.getUser()
}

/** Liest employee_identifier aus dem ID-Token-Claim (gesetzt per Keycloak Protocol Mapper). */
export function getEmployeeIdentifier(user: User): string | null {
  const claims = user.profile as Record<string, unknown>
  return typeof claims['employee_identifier'] === 'string'
    ? claims['employee_identifier']
    : null
}

/** Nach Redirect von Keycloak: Token aus URL verarbeiten und gespeicherten User zurueckgeben. */
export async function handleCallback(): Promise<User | null> {
  try {
    const user = await userManager.signinRedirectCallback()
    // URL bereinigen -- kein code/state mehr in der Adresszeile
    window.history.replaceState({}, '', '/')
    return user
  } catch {
    return null
  }
}

export const SESSION_EXPIRED_MESSAGE = 'Sitzung abgelaufen, bitte neu anmelden'

export class SessionExpiredError extends Error {
  constructor() {
    super(SESSION_EXPIRED_MESSAGE)
    this.name = 'SessionExpiredError'
  }
}

/** Token gilt als "fast abgelaufen", wenn weniger als so viele Sekunden Restlaufzeit bleiben. */
export const RENEW_LEEWAY_SECONDS = 90

const PERMANENT_OIDC_ERRORS = new Set([
  'invalid_grant',
  'login_required',
  'interaction_required',
  'consent_required',
  'invalid_client',
  'unauthorized_client',
])

/**
 * Endgueltig ungueltig (Refresh-Token widerrufen/abgelaufen, SSO-Session weg, Iframe-Timeout
 * ohne refresh_token)?
 * Netzwerkfehler (offline, Server nicht erreichbar, 5xx) sind NICHT endgueltig: dann bleibt
 * die Anmeldung bestehen und der Aufrufer versucht es spaeter erneut.
 */
export function isPermanentAuthFailure(e: unknown, hasRefreshToken = true): boolean {
  if (e instanceof SessionExpiredError) return true
  const err = e as { error?: unknown; name?: unknown } | null
  if (typeof err?.error === 'string') return PERMANENT_OIDC_ERRORS.has(err.error)
  // ErrorTimeout: mit refresh_token ist es ein langsamer Token-Endpunkt (schwaches Netz) und
  // voruebergehend. Nur im Iframe-Weg ohne refresh_token (keine /silent-Route) endgueltig.
  return err?.name === 'ErrorTimeout' && !hasRefreshToken
}

export class AuthTemporarilyUnavailableError extends Error {
  readonly cause: unknown
  constructor(cause: unknown) {
    super('Keine Verbindung zum Anmeldeserver, bitte spaeter erneut versuchen')
    this.name = 'AuthTemporarilyUnavailableError'
    this.cause = cause
  }
}

function needsRenew(user: User): boolean {
  if (user.expired) return true
  return typeof user.expires_in === 'number' && user.expires_in < RENEW_LEEWAY_SECONDS
}

/**
 * Liefert ein gueltiges access_token. Ohne forceRenew wird das gespeicherte Token benutzt,
 * solange es nicht (fast) abgelaufen ist; sonst (oder mit forceRenew) wird per signinSilent
 * erneuert (mit offline_access per Refresh-Token, ohne Iframe).
 * Endgueltig ungueltig oder kein User: SessionExpiredError (Re-Login noetig).
 * Nur voruebergehend nicht erreichbar: AuthTemporarilyUnavailableError (angemeldet bleiben).
 */
export async function getFreshAccessToken(forceRenew = false): Promise<string> {
  let user: User | null = null
  try {
    user = await userManager.getUser()
    if (!user || forceRenew || needsRenew(user)) {
      user = await renewSingleFlight()
    }
  } catch (e) {
    if (isPermanentAuthFailure(e, hasRefreshToken(user))) throw new SessionExpiredError()
    throw new AuthTemporarilyUnavailableError(e)
  }
  if (!user) throw new SessionExpiredError()
  return user.access_token
}

function hasRefreshToken(user: User | null): boolean {
  return !!user?.refresh_token
}

// Single-Flight: parallele Aufrufer (callWithFreshToken, Keeper) teilen sich ein signinSilent.
let renewInFlight: Promise<User | null> | null = null

function renewSingleFlight(): Promise<User | null> {
  if (!renewInFlight) {
    renewInFlight = userManager.signinSilent().finally(() => {
      renewInFlight = null
    })
  }
  return renewInFlight
}

/**
 * Haelt das Token aktiv frisch, nicht erst bei 401: beim Start, per Timer, bei
 * Sichtbarkeits-Wechsel und beim online-Event (Timer pausieren in Hintergrund-/Standby-PWAs,
 * deshalb reicht automaticSilentRenew allein nicht). onSessionExpired wird nur bei endgueltig
 * ungueltiger Anmeldung gerufen. Gibt die Aufraeum-Funktion zurueck.
 */
export function startTokenKeeper(
  onSessionExpired: () => void,
  intervalMs = 60_000,
): () => void {
  let running = false
  let stopped = false
  const check = async () => {
    if (running || stopped) return
    running = true
    try {
      if (await userManager.getUser()) await getFreshAccessToken()
    } catch (e) {
      if (e instanceof SessionExpiredError) {
        // Endgueltig: weiteres Pruefen waere sinnlos (Re-Login noetig) -- Keeper beenden.
        stop()
        onSessionExpired()
      }
      // AuthTemporarilyUnavailableError: offline -- beim naechsten Tick/online erneut
    } finally {
      running = false
    }
  }
  const onVisible = () => {
    if (document.visibilityState === 'visible') void check()
  }
  const timer = setInterval(() => void check(), intervalMs)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('online', check)
  void check()
  function stop() {
    stopped = true
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('online', check)
  }
  return stop
}

function isUnauthorized(e: unknown): boolean {
  return (e as { status?: number } | null)?.status === 401
}

/**
 * Fuehrt einen API-Aufruf mit frischem Token aus. Bei 401 wird das Token genau einmal
 * erneuert und der Aufruf genau einmal wiederholt; schlaegt das fehl, SessionExpiredError.
 */
export async function callWithFreshToken<T>(call: (token: string) => Promise<T>): Promise<T> {
  try {
    return await call(await getFreshAccessToken())
  } catch (e) {
    if (!isUnauthorized(e)) throw e
  }
  const token = await getFreshAccessToken(true)
  try {
    return await call(token)
  } catch (e) {
    if (isUnauthorized(e)) throw new SessionExpiredError()
    throw e
  }
}
